import { useExamCenter } from '@exam-center/exam-center-details';
import {
  useSearchExamCenterDraftProctorRequestsQuery,
  FilterOperator,
  ExamCenterProctorRequestStep,
} from '@exam-center/graphql';
import { ProctorRequestCreationButton } from '@exam-center/proctor-request-creation';
import { useNavigate } from '@exam-center/router';
import { type GridRowParams, type GridEventListener, NoRowsOverlay } from '@shared/components';

import { BaseProctorRequestList, draftColumns, type DraftProctorRequest } from '../../components';
import { ORDER_TYPE, PROCTOR_TYPE_VARIANT_MAP } from '../../constants';
import { useProctorRequestListSearchParams } from '../../hooks';
import { EmptyProctorRequestsOverlay } from '../empty-proctor-requests-overlay';

export default function DraftProctorRequestList() {
  const { examCenter } = useExamCenter();
  const navigate = useNavigate();

  const [
    { page, pageSize, sortOrder, search, proctorType },
    { changePageNumber, changePageSize, changeSorting },
  ] = useProctorRequestListSearchParams();

  // Update this
  const { data, loading } = useSearchExamCenterDraftProctorRequestsQuery({
    variables: {
      searchExamCenterProctorRequestsInput: {
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'editedAt',
          type: ORDER_TYPE[sortOrder],
        },
        filter: {
          searchText: {
            fields: ['fullName'],
            value: search,
          },
          conditions: [
            {
              field: 'examCenterCode',
              value: examCenter.code,
              operator: FilterOperator.Equal,
            },
            {
              field: 'step',
              value: ExamCenterProctorRequestStep.Draft,
              operator: FilterOperator.Equal,
            },
            {
              field: 'type',
              value: proctorType.length > 0 ? proctorType : null,
              operator: FilterOperator.In,
            },
          ].filter((condition) => condition.value !== null),
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const examCenterProctorRequests =
    data?.ecSearchExamCenterProctorRequests.examCenterProctorRequests ?? [];

  const examCenterProctorRequestItems = examCenterProctorRequests.map((proctorRequest) => ({
    id: proctorRequest.id,
    refCode: proctorRequest.refCode,
    name: [
      proctorRequest.titleTh,
      proctorRequest.firstNameTh,
      proctorRequest.middleNameTh,
      proctorRequest.lastNameTh,
    ]
      .filter(Boolean)
      .join(' '),
    type: PROCTOR_TYPE_VARIANT_MAP[proctorRequest.type],
    status: 'draft',
    editedAt: proctorRequest.editedAt,
    editedBy: proctorRequest.editedUser
      ? `${proctorRequest.editedUser?.firstname} ${proctorRequest.editedUser?.lastname}`
      : '-',
  }));

  const totalCount = data?.ecSearchExamCenterProctorRequests.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<DraftProctorRequest>,
  ) => {
    navigate(`/:examCenterCode/exam-proctors/requests/:requestId`, {
      params: {
        requestId: params.row.id,
        examCenterCode: examCenter.code,
      },
    });
  };

  const isSearchingOrFiltering = search.length > 0 || proctorType.length > 0;

  return (
    <BaseProctorRequestList
      columns={draftColumns}
      loading={loading}
      rows={examCenterProctorRequestItems}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => {
          if (isSearchingOrFiltering) {
            return <NoRowsOverlay />;
          }

          return (
            <EmptyProctorRequestsOverlay
              title='ยังไม่มีคำขออนุมัติผู้คุมสอบ'
              description='คลิกปุ่มด้านล่างเพื่อสร้างคำขออนุมัติผู้คุมสอบใหม่'
              action={<ProctorRequestCreationButton variant='outlined' size='large' />}
            />
          );
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
